'use client';

import type { FC, ReactNode } from 'react';
import { BottomSheet, Cell, List, Section } from '../../components/ui';
import { cn } from '../../utils/cn';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useXpConfig } from '../../hooks/useXpConfig';
import { formatCreditsNumber } from '../../utils/formatters';
import referralTranslations from './ReferralScreen.translations';
import antiFraudTranslations from './AntiFraudInfo.translations';
import { RiCheckLine } from '../../components/icons';

interface ReferralRulesSheetProps {
  isOpen: boolean;
  onClose: () => void;
  rewardCredits: number;
}

const stripColon = (s: string) => s.replace(/[:：]\s*$/, '');

const MILESTONE_BONUS_CREDITS = 2000;

const Val: FC<{ className?: string; children: ReactNode }> = ({ className, children }) => (
  <span className={cn('font-semibold', className)}>{children}</span>
);

export const ReferralRulesSheet: FC<ReferralRulesSheetProps> = ({ isOpen, onClose, rewardCredits }) => {
  const { t } = useLocalTranslation(referralTranslations);
  const { t: tAf } = useLocalTranslation(antiFraudTranslations);
  const { getReferralInviteXp } = useXpConfig();

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('statsRulesButton')}>
      <Section header={t('bonusesTitle')} className="mb-2">
        <List>
          <Cell after={<Val className="text-accent-yellow">+{formatCreditsNumber(rewardCredits)}</Val>}>
            {stripColon(t('bonusesPerFriend'))}
          </Cell>
          <Cell after={<Val className="text-accent-yellow">+{formatCreditsNumber(rewardCredits + MILESTONE_BONUS_CREDITS)}</Val>}>
            {stripColon(t('bonusesPer10thFriend'))}
          </Cell>
          <Cell after={<Val className="text-accent-green">5%</Val>}>{stripColon(t('bonusesFromDeposits'))}</Cell>
          <Cell after={<Val className="text-accent-yellow">+100</Val>}>{stripColon(t('bonusesPerFriendL2'))}</Cell>
          <Cell after={<Val className="text-accent-green">0.5%</Val>}>{stripColon(t('bonusesFromDepositsL2'))}</Cell>
          <Cell after={<Val className="text-accent-purple">+{getReferralInviteXp()} XP</Val>}>
            {stripColon(t('bonusesXpPerFriend'))}
          </Cell>
        </List>
        <p className="px-4 pt-2 text-[13px] leading-snug text-label-tertiary">{t('activationExplanation')}</p>
      </Section>

      <Section header={tAf('howToAvoidTitle')} className="mb-6">
        <div className="rounded-[22px] bg-bg-secondary p-4 flex flex-col gap-3.5">
          <p className="m-0 text-[14px] leading-relaxed text-label-secondary">
            {tAf('description', { reward: formatCreditsNumber(rewardCredits) })}
          </p>

          <div className="flex flex-col gap-2.5">
            {[tAf('howToAvoidItem1'), tAf('howToAvoidItem2'), tAf('howToAvoidItem3')].map((item, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <RiCheckLine className="shrink-0 mt-0.5 text-base text-accent-green" />
                <p className="m-0 text-[14px] leading-snug text-label-primary">{item}</p>
              </div>
            ))}
          </div>

          <div className="rounded-[16px] bg-accent-blue/10 border border-accent-blue/25 p-3">
            <p className="m-0 text-[13px] leading-snug text-accent-blue">{tAf('howToAvoidInfo')}</p>
          </div>
        </div>
      </Section>
    </BottomSheet>
  );
};
